import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPatch, apiPost } from "../../services/api";
import { useToast } from "../../components/common/ToastProvider";
import {
    ConsultantRow,
    ConsultantServiceFeeRow,
    ConsultantServiceRow,
    DiagnosisOption,
    NewPatientFormState,
    PatientProfile,
    ProcedureSessionRow,
    VisitRecord,
} from "../../components/careflow/types";
import { mapDiagnosisRows, mapSessions, mergeDiagnosisOptions } from "../../components/careflow/utils";
import { getAllProcedures, loadProcedureRegistry, ProcedureEntry } from "../../registry/procedureRegistry";
import { useClinicStore } from "../../store/clinicStore";
import { subscribeCareflowSync } from "../../services/careflowSync";

export function useCareFlowState() {
    const { showToast } = useToast();
    const setActivePatientProfile = useClinicStore((s) => s.setActivePatientProfile);
    const setActiveVisitRecord = useClinicStore((s) => s.setActiveVisitRecord);
    const resetClinicSession = useClinicStore((s) => s.resetSession);
    const storedPatient = useClinicStore((s) => s.activePatient);

    const [patient, setPatient] = useState<PatientProfile | null>(null);
    const [visit, setVisit] = useState<VisitRecord | null>(null);
    const [loadingVisit, setLoadingVisit] = useState(false);
    const hydratedRef = useRef(false);

    // Diagnosis State
    const [diagnosisMaster, setDiagnosisMaster] = useState<DiagnosisOption[]>([]);
    const [commonDiagnoses, setCommonDiagnoses] = useState<DiagnosisOption[]>([]);
    const [diagnosisOptions, setDiagnosisOptions] = useState<DiagnosisOption[]>([]);
    const [visitDiagnoses, setVisitDiagnoses] = useState<DiagnosisOption[]>([]);

    // Treatment Catalog State
    const [catalog, setCatalog] = useState<ProcedureEntry[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [consultants, setConsultants] = useState<ConsultantRow[]>([]);
    const [consultantServices, setConsultantServices] = useState<ConsultantServiceRow[]>([]);
    const [consultantServiceFees, setConsultantServiceFees] = useState<ConsultantServiceFeeRow[]>([]);
    const [sessions, setSessions] = useState<ProcedureSessionRow[]>([]);

    // Boot Data Fetching
    useEffect(() => {
        let active = true;

        const boot = async () => {
            setCatalogLoading(true);
            try {
                await loadProcedureRegistry();
                if (active) setCatalog(getAllProcedures());
            } catch {
                if (active) setCatalog([]);
            } finally {
                if (active) setCatalogLoading(false);
            }

            const [consultantsRes, servicesRes, feesRes, diagnosisMasterRes, commonRes] = await Promise.allSettled([
                apiGet("/api/doctor/consultants"),
                apiGet("/api/manager/consultant-services"),
                apiGet("/api/manager/consultant-service-fees"),
                apiGet("/api/admin/diagnosis"),
                apiGet("/api/doctor/diagnoses/common"),
            ]);

            if (!active) return;

            if (consultantsRes.status === "fulfilled") {
                setConsultants(Array.isArray(consultantsRes.value) ? (consultantsRes.value as ConsultantRow[]) : []);
            }
            if (servicesRes.status === "fulfilled") {
                setConsultantServices(
                    Array.isArray(servicesRes.value) ? (servicesRes.value as ConsultantServiceRow[]) : []
                );
            }
            if (feesRes.status === "fulfilled") {
                setConsultantServiceFees(
                    Array.isArray(feesRes.value) ? (feesRes.value as ConsultantServiceFeeRow[]) : []
                );
            }

            const masterRows =
                diagnosisMasterRes.status === "fulfilled"
                    ? mapDiagnosisRows(diagnosisMasterRes.value).filter((row) => row && row.description)
                    : [];
            const commonRows =
                commonRes.status === "fulfilled" ? mapDiagnosisRows(commonRes.value) : [];

            setDiagnosisMaster(masterRows);
            setCommonDiagnoses(commonRows);
            setDiagnosisOptions(mergeDiagnosisOptions(masterRows, commonRows));
        };

        void boot();
        return () => {
            active = false;
        };
    }, []);

    const loadVisitData = useCallback(
        async (visitId: number) => {
            try {
                const [diagnosesRes, sessionsRes] = await Promise.all([
                    apiGet(`/api/doctor/diagnoses/${visitId}`),
                    apiGet(`/api/doctor/procedures/by-visit/${visitId}`),
                ]);
                setVisitDiagnoses(mapDiagnosisRows(diagnosesRes));
                setSessions(mapSessions(sessionsRes));
            } catch {
                setVisitDiagnoses([]);
                setSessions([]);
            }
        },
        []
    );

    const ensureTodayVisit = useCallback(async (pid: string): Promise<VisitRecord | null> => {
        try {
            const existing = (await apiGet(
                `/api/visits/today/${encodeURIComponent(pid)}`
            )) as VisitRecord | null;
            if (existing?.visit_id) return existing;
        } catch {
            // Create if not found
        }

        const created = (await apiPost(`/api/visits/today/${encodeURIComponent(pid)}`, {
            visit_type: "Old Patient",
            remarks: "Fast billing session",
            created_source: "doctor_ui",
        })) as VisitRecord;

        return created?.visit_id ? created : null;
    }, []);

    const bindPatientContext = useCallback(
        async (nextPatient: PatientProfile) => {
            const pid = String(nextPatient.patient_id || "").trim();
            if (!pid) return;

            setPatient(nextPatient);
            setActivePatientProfile({
                patient_id: nextPatient.patient_id,
                name: nextPatient.name,
                age: nextPatient.age,
                gender: nextPatient.gender,
                mobile: nextPatient.mobile,
                phone: nextPatient.phone,
            });
            setVisit(null);
            setSessions([]);
            setVisitDiagnoses([]);
            setLoadingVisit(true);

            try {
                const ensuredVisit = await ensureTodayVisit(pid);
                if (!ensuredVisit?.visit_id) {
                    showToast({ message: "Unable to create/open today visit.", type: "error" });
                    return;
                }

                setVisit(ensuredVisit);
                if (ensuredVisit) {
                    setActiveVisitRecord({
                        visit_id: ensuredVisit.visit_id,
                        status: ensuredVisit.status,
                        visit_date: ensuredVisit.visit_date,
                    });
                }
                await loadVisitData(Number(ensuredVisit.visit_id));
            } catch {
                showToast({ message: "Failed to load patient workflow.", type: "error" });
            } finally {
                setLoadingVisit(false);
            }
        },
        [ensureTodayVisit, loadVisitData, setActivePatientProfile, setActiveVisitRecord, showToast]
    );

    useEffect(() => {
        if (hydratedRef.current) return;
        hydratedRef.current = true;
        if (storedPatient) {
            void bindPatientContext(storedPatient as PatientProfile);
        }
    }, [storedPatient, bindPatientContext]);

    useEffect(() => {
        const activeVisitId = Number(visit?.visit_id || 0);
        if (!activeVisitId) return;
        const unsubscribe = subscribeCareflowSync((payload) => {
            const incomingVisitId = Number(payload?.visitId || 0);
            if (incomingVisitId && incomingVisitId !== activeVisitId) return;
            if (payload.reason !== "chart_event_saved" && payload.reason !== "chart_event_deleted") return;
            void loadVisitData(activeVisitId);
        });
        return unsubscribe;
    }, [loadVisitData, visit?.visit_id]);

    const updatePatient = useCallback(
        async (pid: string, data: Partial<PatientProfile>) => {
            try {
                const updated = (await apiPatch(`/api/patients/${encodeURIComponent(pid)}`, data)) as PatientProfile;
                setPatient(updated);
                setActivePatientProfile({
                    patient_id: updated.patient_id,
                    name: updated.name,
                    age: updated.age,
                    gender: updated.gender,
                    mobile: updated.mobile,
                    phone: updated.phone,
                });
                showToast({ message: "Patient updated successfully.", type: "success" });
                return updated;
            } catch (err) {
                showToast({ message: "Failed to update patient.", type: "error" });
                throw err;
            }
        },
        [setActivePatientProfile, showToast]
    );

    const resetContext = useCallback(() => {
        setPatient(null);
        setVisit(null);
        setSessions([]);
        setVisitDiagnoses([]);
        resetClinicSession();
    }, [resetClinicSession]);

    return {
        patient,
        visit,
        loadingVisit,
        catalog,
        catalogLoading,
        consultants,
        consultantServices,
        consultantServiceFees,
        sessions,
        setSessions,
        diagnosisMaster,
        commonDiagnoses,
        diagnosisOptions,
        setDiagnosisOptions,
        visitDiagnoses,
        setVisitDiagnoses,
        bindPatientContext,
        resetContext,
        updatePatient,
        loadVisitData,
    };
}
